/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';

import ButtonsUseStyles from 'consts/styles/Buttons';
import RequiredField from 'components/common/components/RequiredField';
import SendEmail from 'components/user/admin/users/SendEmail';
import UserSessionDataHandler from 'auth/UserSessionsDataHandler';
import { addAlert } from 'store/actions/alert/alerts';
import { authPatchRequestWithParam } from 'helpers/apiRequests';
import { formStyles } from 'consts/styles/Form';
import { fromUpperCaseToUnderScore } from 'helpers/variablesNamesFit';
import { userStyles } from 'consts/styles/User';
import { validateCheck } from 'components/user/admin/organizations/components/Helpers';

const EditUser = () => {
  const formClasses = formStyles();
  const classesUser = userStyles();
  const classesButtons = ButtonsUseStyles();

  const dispatch = useDispatch();
  const [userData] = React.useState(UserSessionDataHandler.getUserDataObject());
  const [values, setValues] = React.useState({
    uid: userData.uid,
    firstName: userData.firstName,
    lastName: userData.lastName,
    username: userData.username,
    phone: userData.phone,
    email: userData.email,
    city: userData.address?.city,
    street: userData.address?.street,
    number: userData.address?.number,
    password: '',
    rePassword: '',
  });

  const useString = () => {
    const language = useSelector(state => state.language);
    return language;
  };

  const [alertsSubtitles] = React.useState(useString().alerts);
  const [subtitles] = React.useState(useString().userProfile);
  const [errorsSubtitles] = React.useState(useString().areas.placeholderForm);

  const [errors, setErrors] = React.useState({
    firstName: false,
    lastName: false,
    username: false,
    phone: false,
    email: false,
    password: false,
    rePassword: false,
  });

  const handleChange = event => {
    const { target } = event;
    const { name, value } = target;
    setValues({
      ...values,
      [name]: value,
    });
    if (!validateCheck(name, value))
      setErrors({
        ...errors,
        [name]: true,
      });
    else
      setErrors({
        ...errors,
        [name]: false,
      });
  };

  const isDataValid = () => {
    let isValid = true;
    Object.keys(errors).forEach(key => {
      if (errors[key]) isValid = false;
    });

    if (!isValid) return false;
    return true;
  };

  const updateUserDataInStorage = data => {
    Object.keys(userData).forEach(key => {
      if (data[key]) userData[key] = data[key];
    });
    UserSessionDataHandler.saveUserDataObject(userData);
  };

  const add = async () => {
    if (!isDataValid()) {
      return;
    }

    const data = { ...values };

    data.address = {
      city: values.city,
      street: values.street,
      number: values.number,
    };

    if (data.password === '') delete data.password;

    delete data.city;
    delete data.street;
    delete data.number;
    delete data.rePassword;
    const parsedData = fromUpperCaseToUnderScore(data);

    const result = await authPatchRequestWithParam(
      'editUser',
      data.uid,
      parsedData
    );
    if (result.status === 200) {
      dispatch(
        addAlert({
          title: alertsSubtitles.addDataSuccess.title_,
          desc: alertsSubtitles.addDataSuccess.desc_,
          type: 'success',
        })
      );
      updateUserDataInStorage(data);
    } else
      dispatch(
        addAlert({
          title: alertsSubtitles[result.data.code].title_,
          desc: alertsSubtitles[result.data.code].desc_,
          type: 'error',
        })
      );
  };

  return (
    <div className={`${classesUser.User}__wrapper__content__form-container`}>
      <Container className={`${classesUser.User}__wrapper__content__container`}>
        <Typography
          variant="h2"
          className={`${formClasses.Form}__text-field ${formClasses.Form}__text-field__title`}
        >
          {subtitles.header_}
        </Typography>
        <div>
          <form
            className={`${classesUser.User}__wrapper__content__form-container__form`}
            noValidate
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <label
                  className={clsx(`${formClasses.Form}__label`, {
                    [`${formClasses.Form}__text-field__form-helper--error`]: errors.firstName,
                  })}
                  htmlFor="firstName"
                >
                  {subtitles.formFields.firstName_}*
                </label>
                <TextField
                  autoComplete="firstName"
                  error={errors.firstName}
                  name="firstName"
                  variant="outlined"
                  required
                  fullWidth
                  id="firstName"
                  onChange={handleChange}
                  value={values.firstName}
                  helperText={
                    errors.firstName
                      ? `${errorsSubtitles.required}${errorsSubtitles.text}`
                      : ''
                  }
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  FormHelperTextProps={{
                    classes: {
                      error: `${formClasses.Form}__text-field__form-helper--error`,
                    },
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <label
                  className={clsx(`${formClasses.Form}__label`, {
                    [`${formClasses.Form}__text-field__form-helper--error`]: errors.lastName,
                  })}
                  htmlFor="lastName"
                >
                  {subtitles.formFields.lastName_}*
                </label>
                <TextField
                  variant="outlined"
                  required
                  error={errors.lastName}
                  fullWidth
                  id="lastName"
                  name="lastName"
                  autoComplete="lastName"
                  onChange={handleChange}
                  value={values.lastName}
                  helperText={
                    errors.lastName
                      ? `${errorsSubtitles.required}${errorsSubtitles.text}`
                      : ''
                  }
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  FormHelperTextProps={{
                    classes: {
                      error: `${formClasses.Form}__text-field__form-helper--error`,
                    },
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <label
                  className={clsx(`${formClasses.Form}__label`, {
                    [`${formClasses.Form}__text-field__form-helper--error`]: errors.username,
                  })}
                  htmlFor="username"
                >
                  {subtitles.formFields.username_}*
                </label>
                <TextField
                  autoComplete="username"
                  error={errors.username}
                  name="username"
                  variant="outlined"
                  required
                  fullWidth
                  id="username"
                  onChange={handleChange}
                  value={values.username}
                  helperText={
                    errors.username
                      ? `${errorsSubtitles.required}${errorsSubtitles.text}`
                      : ''
                  }
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  FormHelperTextProps={{
                    classes: {
                      error: `${formClasses.Form}__text-field__form-helper--error`,
                    },
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <label
                  className={clsx(`${formClasses.Form}__label`, {
                    [`${formClasses.Form}__text-field__form-helper--error`]: errors.phone,
                  })}
                  htmlFor="phone"
                >
                  {subtitles.formFields.phone_}*
                </label>
                <TextField
                  variant="outlined"
                  error={errors.phone}
                  fullWidth
                  id="phone"
                  name="phone"
                  autoComplete="phone"
                  onChange={handleChange}
                  value={values.phone}
                  helperText={
                    errors.phone
                      ? `${errorsSubtitles.required}${errorsSubtitles.phone}`
                      : ''
                  }
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  FormHelperTextProps={{
                    classes: {
                      error: `${formClasses.Form}__text-field__form-helper--error`,
                    },
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <label
                  className={clsx(`${formClasses.Form}__label`, {
                    [`${formClasses.Form}__text-field__form-helper--error`]: errors.email,
                  })}
                  htmlFor="email"
                >
                  {subtitles.formFields.email_}*
                </label>
                <TextField
                  autoComplete="email"
                  name="email"
                  error={errors.email}
                  variant="outlined"
                  required
                  fullWidth
                  id="email"
                  type="email"
                  onChange={handleChange}
                  value={values.email}
                  helperText={
                    errors.email
                      ? `${errorsSubtitles.required}${errorsSubtitles.email}`
                      : ''
                  }
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  FormHelperTextProps={{
                    classes: {
                      error: `${formClasses.Form}__text-field__form-helper--error`,
                    },
                  }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <label className={`${formClasses.Form}__label`} htmlFor="city">
                  {subtitles.formFields.city_}
                </label>
                <TextField
                  autoComplete="fcity"
                  name="city"
                  variant="outlined"
                  fullWidth
                  id="city"
                  onChange={handleChange}
                  value={values.city}
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <label
                  className={`${formClasses.Form}__label`}
                  htmlFor="street"
                >
                  {subtitles.formFields.street_}
                </label>
                <TextField
                  variant="outlined"
                  fullWidth
                  id="street"
                  name="street"
                  autoComplete="street"
                  onChange={handleChange}
                  value={values.street}
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <label
                  className={`${formClasses.Form}__label`}
                  htmlFor="number"
                >
                  {subtitles.formFields.number_}{' '}
                </label>
                <TextField
                  className={`${formClasses.FormCalc}__input`}
                  variant="outlined"
                  fullWidth
                  id="number"
                  name="number"
                  autoComplete="number"
                  onChange={handleChange}
                  value={values.number}
                  InputProps={{
                    classes: {
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    },
                  }}
                  size="small"
                />
              </Grid>
            </Grid>

            <Button
              type="button"
              fullWidth
              className={`${classesButtons.Buttons} ${classesButtons.Buttons}--color ${classesButtons.ButtonsRectangle}`}
              onClick={add}
            >
              {subtitles.button_}
            </Button>
          </form>
        </div>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <RequiredField />
          </Grid>
          <Grid item>
            <SendEmail emailValue={values.email} />
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};
const ExportedEditUser = React.forwardRef((props, ref) => (
  <EditUser {...props} {...ref} />
));
export default ExportedEditUser;
